// map-import — state machine + types + error codes for the floor plan
// import pipeline. See `docs/specs/floor-plan-import.md`.
//
// Shared between the location-api worker (which advances job state)
// and the frontend (which renders the progress bar). Both sides import
// the same enum + weight map so progress can't drift.

// ── Status enum (24 states) ──────────────────────────────────────────

export const MAP_IMPORT_STATUSES = [
  "created",
  "uploaded",
  "queued",
  "preprocessing_started",
  "image_normalized",
  "furniture_cleanup_started",
  "furniture_cleanup_completed",
  "binarization_started",
  "binarization_completed",
  "line_cleanup_started",
  "line_cleanup_completed",
  "structure_extraction_started",
  "wall_detection_completed",
  "door_detection_completed",
  "room_segmentation_completed",
  "vectorization_started",
  "vectorization_completed",
  "scene_graph_generated",
  "ready_for_review",
  "approved",
  "saved",
  "failed",
  "cancelled",
] as const;

export type MapImportStatus = typeof MAP_IMPORT_STATUSES[number];

const STATUS_SET: Set<string> = new Set(MAP_IMPORT_STATUSES);

export function isMapImportStatus(s: string): s is MapImportStatus {
  return STATUS_SET.has(s);
}

// ── Progress weights ─────────────────────────────────────────────────
//
// Per the spec; intermediate "_started" states reuse the prior
// completion's percent (no synthetic bump). `failed` and `cancelled`
// freeze at whatever the row's progress_percent already was.

export const MAP_IMPORT_PROGRESS_PERCENT: Readonly<Record<MapImportStatus, number>> = Object.freeze({
  created:                       0,
  uploaded:                      5,
  queued:                        8,
  preprocessing_started:        12,
  image_normalized:             18,
  furniture_cleanup_started:    25,
  furniture_cleanup_completed:  38,
  binarization_started:         38,
  binarization_completed:       45,
  line_cleanup_started:         45,
  line_cleanup_completed:       52,
  structure_extraction_started: 58,
  wall_detection_completed:     68,
  door_detection_completed:     76,
  room_segmentation_completed:  84,
  vectorization_started:        84,
  vectorization_completed:      91,
  scene_graph_generated:        96,
  ready_for_review:            100,
  approved:                    100,
  saved:                       100,
  failed:                        0,  // frozen at last successful %
  cancelled:                     0,  // frozen at last successful %
});

/** Returns the canonical % for this status. Caller may pass through
 *  the previously-known % when the status is `failed` / `cancelled` so
 *  the bar doesn't snap back to 0 visually. */
export function progressForStatus(s: MapImportStatus, lastKnown = 0): number {
  if (s === "failed" || s === "cancelled") return lastKnown;
  return MAP_IMPORT_PROGRESS_PERCENT[s];
}

// ── State transitions ────────────────────────────────────────────────
//
// Strict ordering — the worker may only advance to a state listed in
// the current state's allowed[] (plus the universal terminal states
// `failed` / `cancelled`, which can transition from anywhere).

const TERMINAL_FROM_ANYWHERE: ReadonlyArray<MapImportStatus> = ["failed", "cancelled"];

const TRANSITIONS: Readonly<Record<MapImportStatus, ReadonlyArray<MapImportStatus>>> = Object.freeze({
  created:                      ["uploaded", ...TERMINAL_FROM_ANYWHERE],
  uploaded:                     ["queued", ...TERMINAL_FROM_ANYWHERE],
  queued:                       ["preprocessing_started", ...TERMINAL_FROM_ANYWHERE],
  preprocessing_started:        ["image_normalized", ...TERMINAL_FROM_ANYWHERE],
  image_normalized:             ["furniture_cleanup_started", ...TERMINAL_FROM_ANYWHERE],
  furniture_cleanup_started:    ["furniture_cleanup_completed", ...TERMINAL_FROM_ANYWHERE],
  furniture_cleanup_completed:  ["binarization_started", ...TERMINAL_FROM_ANYWHERE],
  binarization_started:         ["binarization_completed", ...TERMINAL_FROM_ANYWHERE],
  binarization_completed:       ["line_cleanup_started", ...TERMINAL_FROM_ANYWHERE],
  line_cleanup_started:         ["line_cleanup_completed", ...TERMINAL_FROM_ANYWHERE],
  line_cleanup_completed:       ["structure_extraction_started", ...TERMINAL_FROM_ANYWHERE],
  structure_extraction_started: ["wall_detection_completed", ...TERMINAL_FROM_ANYWHERE],
  wall_detection_completed:     ["door_detection_completed", ...TERMINAL_FROM_ANYWHERE],
  door_detection_completed:     ["room_segmentation_completed", ...TERMINAL_FROM_ANYWHERE],
  room_segmentation_completed:  ["vectorization_started", ...TERMINAL_FROM_ANYWHERE],
  vectorization_started:        ["vectorization_completed", ...TERMINAL_FROM_ANYWHERE],
  vectorization_completed:      ["scene_graph_generated", ...TERMINAL_FROM_ANYWHERE],
  scene_graph_generated:        ["ready_for_review", ...TERMINAL_FROM_ANYWHERE],
  ready_for_review:             ["approved", "cancelled"],  // user-driven
  approved:                     ["saved", "failed"],
  saved:                        [],   // terminal success
  failed:                       ["queued"],  // retry restarts pipeline
  cancelled:                    [],   // terminal — must start a new job
});

export function canTransition(from: MapImportStatus, to: MapImportStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

// ── Error codes ──────────────────────────────────────────────────────

export const MAP_IMPORT_ERROR_CODES = [
  "IMAGE_TOO_LOW_RESOLUTION",
  "IMAGE_TOO_BLURRY",
  "UNSUPPORTED_FILE_TYPE",
  "PREPROCESSING_FAILED",
  "FURNITURE_CLEANUP_FAILED",
  "STRUCTURE_EXTRACTION_FAILED",
  "VECTORIZE_FAILED",
  "SCENE_GRAPH_FAILED",
  "STORAGE_FAILED",
  "JOB_TIMEOUT",
  "PERMISSION_DENIED",
] as const;

export type MapImportErrorCode = typeof MAP_IMPORT_ERROR_CODES[number];

// ── Step descriptors for the UI step-list ────────────────────────────

export interface MapImportStepDescriptor {
  /** Canonical step key — multiple statuses can share a step (e.g.
   *  furniture_cleanup_started + furniture_cleanup_completed both belong
   *  to step "furniture_cleanup"). */
  key: string;
  /** zh-TW label rendered in the UI. */
  label: string;
  /** The status emitted when this step finishes. The UI marks the step
   *  "completed" once the job's status has reached or passed it. */
  completes_at: MapImportStatus;
}

/** The seven user-visible steps. `image_upload` is step 1; the rest
 *  match the pipeline stages. Used by MapImportProgress to render the
 *  step-list with pending/processing/completed/failed states. */
export const MAP_IMPORT_STEPS: ReadonlyArray<MapImportStepDescriptor> = [
  { key: "image_upload",         label: "圖片上傳",            completes_at: "uploaded" },
  { key: "preprocessing",        label: "前處理 / 正規化",     completes_at: "image_normalized" },
  { key: "furniture_cleanup",    label: "家具清除",            completes_at: "furniture_cleanup_completed" },
  { key: "binarization",         label: "高對比黑白化",        completes_at: "binarization_completed" },
  { key: "line_cleanup",         label: "線條清理",            completes_at: "line_cleanup_completed" },
  { key: "structure_extraction", label: "結構抽取（牆 / 門 / 房）", completes_at: "room_segmentation_completed" },
  { key: "vectorization",        label: "向量化 + Scene graph", completes_at: "scene_graph_generated" },
];

/** Numeric ordinal — used to compare "has the job reached step X yet". */
export function statusOrdinal(s: MapImportStatus): number {
  return MAP_IMPORT_STATUSES.indexOf(s);
}

// ── Row shape (D1 + API contract) ────────────────────────────────────

export interface MapImportJobRow {
  id: string;
  tenant_id: string;
  site_id: string | null;
  building_id: string | null;
  floor_id: string | null;
  status: MapImportStatus;
  progress_percent: number;
  current_step: string | null;
  current_message: string | null;
  error_code: MapImportErrorCode | null;
  error_message: string | null;
  warning_messages: string | null;  // JSON-stringified array
  source_image_r2_key: string | null;
  cleaned_image_r2_key: string | null;
  vector_svg_r2_key: string | null;
  scene_graph_r2_key: string | null;
  created_by_user_id: string | null;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
}
